import { useQuery } from "@apollo/client/react";
import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router";

import { constantsData, selectionsData } from "@/data";
import { getContributorsListQuery } from "@/graph";
import { openAlert, setSelection, setSelectionLoading } from "@/redux/actions";
import { displayErrorHandler } from "@/utils";

const useContributorsList = (isPaginated = true, hasAutoCompleteMenu = false, searchValue) => {
    const [fetched, setFetched] = useState(false);

    const [params] = useSearchParams();

    const pageParam = parseInt(params.get("page")) || 1;

    const limitParam = parseInt(params.get("limit")) || 25;

    const [page, setPage] = useState(pageParam);

    const [limit, setLimit] = useState(limitParam);

    const dispatchedDataRef = useRef(null);

    const { t } = useTranslation();

    const dispatch = useDispatch();

    const selections = useSelector((state) => state.selectionsReducer);

    const { isAuthenticated } = useAuth0();

    const { contributors: contributorsKey } = selectionsData.keys;

    const { data, error, loading, refetch } = useQuery(getContributorsListQuery, {
        skip: !isAuthenticated,
        variables: {
            ...(isPaginated && {
                elementsPerPage: limit,
                page: page - 1,
            }),
            orderBy: { created_at: "desc" },
            where: {
                ...(searchValue && {
                    name: { contains: searchValue },
                }),
                ...(searchValue && {
                    name_ar: { contains: searchValue }, //eslint-disable-line
                }),
            },
        },
    });

    if (data?.listContributors && dispatchedDataRef.current !== data) {
        dispatchedDataRef.current = data;

        const fetchedList = data.listContributors.posts?.map(({ appleId, id, name, name_ar }) => ({
            appleId,
            label: name,
            nameAr: name_ar,
            value: id,
        }));

        if (!isPaginated || hasAutoCompleteMenu) {
            dispatch(
                setSelection({
                    [contributorsKey]:
                        selections?.[contributorsKey]?.length > 0 && hasAutoCompleteMenu
                            ? selections?.[contributorsKey].concat(fetchedList)
                            : fetchedList,
                }),
            );
        }

        setFetched(true);

        dispatch(setSelectionLoading(contributorsKey, false));
    }

    useEffect(() => {
        if (data && !loading) {
            dispatch(setSelectionLoading(contributorsKey, false));
        }
    }, [error, data, loading]); // eslint-disable-line

    useEffect(
        () => {
            if (error?.message === constantsData.failedToFetch && !loading) {
                displayErrorHandler(t("messages.networkFailure"), dispatch, openAlert);
            }
        },
        [error, data], // eslint-disable-line
    );

    return {
        count: data?.listContributors?.meta?.resultCount,
        data: data?.listContributors?.posts,
        error,
        fetched,
        limit,
        loading,
        page: data?.listContributors?.meta?.currentPage,
        refetch,
        searchKey: "name",
        setFetched,
        setLimit,
        setPage,
    };
};

export default useContributorsList;
